
import { useEffect } from 'react';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';

const AfricanUrbanisationChallengesPage = () => {
  useEffect(() => {
    document.title = 'African Urbanisation Challenges - Investment Opportunities | TS Dotyeni International';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Comprehensive analysis of African urbanisation trends and the tremendous investment opportunities they create in rural development. Expert insights from TS Dotyeni International on sustainable urban-rural investment strategies.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'African urbanisation, rural investment, urban development, investment opportunities, sustainable development');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-emerald-900 to-emerald-700 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/52de9aeecee3bfc5a5fe6beaf647061d.jpg')`
          }}
        ></div>
        <div className="absolute inset-0 bg-emerald-900/70"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            African Urbanisation Challenges
          </h1>
          <p className="text-xl text-emerald-100 max-w-3xl mx-auto">
            Understanding the rapid pace of African urbanisation and the unprecedented opportunities it creates for strategic rural investment
          </p>
        </div>
      </section>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Executive Summary */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Executive Summary</h2>
          <div className="bg-emerald-50 rounded-2xl p-8 mb-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              Africa is experiencing the fastest urbanisation rate globally, with urban populations expected to triple by 2050. This unprecedented demographic shift presents both challenges and extraordinary investment opportunities, particularly in rural development that supports sustainable urban growth.
            </p>
          </div>
        </section>

        {/* Key Statistics */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Statistics &amp; Trends</h2>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="text-3xl font-bold text-emerald-600 mb-2">4.2%</div>
              <div className="text-gray-900 font-semibold mb-2">Annual Urban Growth Rate</div>
              <div className="text-gray-600">Fastest urbanisation globally, creating massive infrastructure demands</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="text-3xl font-bold text-emerald-600 mb-2">1.3B</div>
              <div className="text-gray-900 font-semibold mb-2">Urban Population by 2050</div>
              <div className="text-gray-600">Triple the current urban population, requiring sustainable rural support</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="text-3xl font-bold text-emerald-600 mb-2">$2.5T</div>
              <div className="text-gray-900 font-semibold mb-2">Infrastructure Investment Needed</div>
              <div className="text-gray-600">Massive capital requirements for sustainable urban-rural integration</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="text-3xl font-bold text-emerald-600 mb-2">70%</div>
              <div className="text-gray-900 font-semibold mb-2">Rural Population Still</div>
              <div className="text-gray-600">Majority still rural, presenting untapped investment potential</div>
            </div>
          </div>
        </section>

        {/* Main Analysis */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Urbanisation Challenge</h2>
          
          <div className="mb-8">
            <img 
              src="https://readdy.ai/api/search-image?query=African%20city%20skyline%20showing%20rapid%20urban%20development%20with%20informal%20settlements%2C%20construction%20cranes%2C%20modern%20buildings%20mixed%20with%20traditional%20housing%2C%20demographic%20transition%20visualization%2C%20urban%20planning%20challenges&width=800&height=400&seq=urban-001&orientation=landscape"
              alt="African urbanisation challenges"
              className="w-full h-64 object-cover object-top rounded-xl mb-6"
            />
          </div>

          <h3 className="text-2xl font-semibold text-gray-900 mb-4">Unprecedented Growth Pressures</h3>
          <p className="text-gray-700 leading-relaxed mb-6">
            African cities are growing at an unprecedented rate, with urban populations increasing by 4.2% annually - nearly double the global average. This rapid growth creates immense pressure on urban infrastructure, housing, and services, while simultaneously creating massive opportunities for strategic rural investment that can support sustainable urban development.
          </p>

          <h3 className="text-2xl font-semibold text-gray-900 mb-4">The Rural-Urban Connection</h3>
          <p className="text-gray-700 leading-relaxed mb-6">
            Successful urbanisation requires robust rural economies that can supply food, raw materials, and sustainable migration patterns. Countries with strong rural economies experience more balanced and sustainable urban growth, creating win-win scenarios for both urban and rural development.
          </p>

          <h3 className="text-2xl font-semibold text-gray-900 mb-4">Infrastructure Deficit</h3>
          <p className="text-gray-700 leading-relaxed mb-6">
            The African Development Bank estimates that Africa needs $130-170 billion annually in infrastructure investment. Much of this investment opportunity lies in rural areas that can support urban growth through improved transportation, energy, and communication networks.
          </p>
        </section>

        {/* Investment Opportunities */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Investment Opportunities</h2>
          
          <div className="space-y-8">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Agricultural Value Chains</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Urban food demand is growing exponentially, creating massive opportunities for rural agricultural processing, storage, and distribution facilities. Investment in rural agricultural infrastructure can generate 15-25% returns while supporting urban food security.
              </p>
              <div className="flex items-center text-emerald-600 font-medium">
                <i className="ri-arrow-right-line mr-2"></i>
                Expected ROI: 15-25%
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Transportation Networks</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Connecting rural areas to urban markets through improved transportation infrastructure creates immediate value for rural communities while supporting urban supply chains. Road, rail, and logistics investments show strong returns in high-growth corridors.
              </p>
              <div className="flex items-center text-emerald-600 font-medium">
                <i className="ri-arrow-right-line mr-2"></i>
                Expected ROI: 18-28%
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Energy Infrastructure</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Rural energy projects, particularly renewable energy, can serve both rural communities and growing urban centers. Solar, wind, and mini-grid projects in rural areas offer excellent returns while supporting sustainable development.
              </p>
              <div className="flex items-center text-emerald-600 font-medium">
                <i className="ri-arrow-right-line mr-2"></i>
                Expected ROI: 20-30%
              </div>
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Success Stories</h2>
          
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Rwanda's Rural-Urban Integration</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Rwanda's strategic investment in rural infrastructure has supported sustainable urban growth while maintaining rural prosperity. Rural road networks increased agricultural productivity by 40% while supporting urban food security.
            </p>
            <div className="text-emerald-600 font-medium">Result: 25% increase in rural incomes, 30% reduction in urban food costs</div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Ghana's Agricultural Processing Zones</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Strategic placement of agricultural processing facilities in rural areas created jobs, reduced urban migration pressure, and improved food security for growing cities like Accra and Kumasi.
            </p>
            <div className="text-emerald-600 font-medium">Result: 35% increase in rural employment, 20% improvement in urban food supply</div>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Strategic Recommendations</h2>
          <div className="bg-emerald-50 rounded-2xl p-8">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              African urbanisation presents a unique window of opportunity for strategic rural investment. By investing in rural infrastructure, agricultural value chains, and energy systems, investors can achieve strong financial returns while supporting sustainable urban development.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              The key is to view rural and urban development as interconnected systems, where strategic rural investment creates the foundation for sustainable urban growth and long-term economic prosperity.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <div className="bg-emerald-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-semibold mb-4">Ready to Invest in Africa's Future?</h3>
            <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">
              Join TS Dotyeni International in creating sustainable investment solutions that support both rural development and urban growth.
            </p>
            <button className="bg-white text-emerald-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-semibold whitespace-nowrap cursor-pointer">
              Schedule Investment Consultation
            </button>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
};

export default AfricanUrbanisationChallengesPage;
